import { useCallback, useEffect, useRef, useState } from 'react';
import { callBackend } from '../utils/api';
import { canonicalJsonObject, createOptimizerOperationId } from '../utils/optimizerConstraintEditor';
import { optimizerErrorCode } from '../components/optimizer/optimizerPortfolioMutationContract';
import { designPublicMessage, publicCandidate, requireStudy, searchPortfolioSelection } from '../utils/portfolioDesign';

const path = (id) => `optimizer_design/studies/${encodeURIComponent(id)}`;
const active = (status) => ['queued', 'running', 'cancel_requested'].includes(status);

export default function usePortfolioDesign(bridge = {}, enabled = true) {
    const [study, setStudy] = useState(null);
    const [capturedBaseline, setBaseline] = useState(null);
    const baseline = study ? capturedBaseline : bridge.baseline ?? null;
    const [candidate, setCandidate] = useState(null);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);
    const [stage, setStage] = useState(null);
    const epoch = useRef(0);
    const selection = useRef(0);
    const creating = useRef(null);
    const bridgeRef = useRef(bridge);
    bridgeRef.current = bridge;
    const install = useCallback((value) => {
        const next = requireStudy(value);
        setStudy((previous) => {
            const retained = previous?.study_id === next.study_id ? previous : {};
            const exposures = new Map((retained.trials ?? []).map((trial) => [trial.index, trial.factor_exposures_pct_aum]));
            return { ...retained, ...next, trials: next.trials.map((trial) => ({
                ...trial, factor_exposures_pct_aum: trial.factor_exposures_pct_aum ?? exposures.get(trial.index),
            })) };
        });
        if (next.baseline) setBaseline(next.baseline);
        return next;
    }, []);

    const clear = useCallback(() => {
        ++epoch.current; ++selection.current;
        setStudy(null); setBaseline(null); setCandidate(null); setStage(null); setError(null); setBusy(false);
        creating.current = null;
    }, []);
    useEffect(() => {
        clear();
        if (!enabled) return undefined;
        const version = epoch.current;
        setBusy(true);
        void callBackend('optimizer_design/current', 'GET').then((value) => {
            if (epoch.current === version && value.study) install(value.study);
        }).catch((err) => { if (epoch.current === version) setError(err.message); })
            .finally(() => { if (epoch.current === version) setBusy(false); });
        return () => { ++epoch.current; ++selection.current; };
    }, [bridge.scopeKey, bridge.experimentGeneration, install, clear, enabled]);

    const forgetExpired = useCallback((id, message) => {
        if (bridgeRef.current.currentResult?.studyId === id) bridgeRef.current.clearCurrentResult();
        clear();
        setError(message);
    }, [clear]);
    useEffect(() => {
        if (!study?.expires_at) return undefined;
        const timer = setTimeout(() => forgetExpired(study.study_id,
            'This experiment expired. Saved portfolios remain available; start a new experiment to explore again.'),
        Math.max(0, new Date(study.expires_at).getTime() - Date.now()));
        return () => clearTimeout(timer);
    }, [study?.study_id, study?.expires_at, forgetExpired]);

    // A saved receipt can identify the displayed search run without clicking it
    // again. Fetch its chart data read-only; never reselect or rerun the portfolio.
    const { loadedTrial } = searchPortfolioSelection({ study, currentResult: bridge.currentResult,
        currentRunOperationId: bridge.currentRunOperationId,
        currentPortfolioLoaded: Boolean(bridge.currentResult || bridge.currentRunOperationId) });
    const loadedIndex = loadedTrial?.index;
    const hasExposures = Array.isArray(loadedTrial?.factor_exposures_pct_aum);
    useEffect(() => {
        if (loadedIndex == null || hasExposures) return undefined;
        const id = study.study_id;
        const version = epoch.current;
        let stopped = false;
        void callBackend(`${path(id)}/trials/${loadedIndex}`, 'GET').then(publicCandidate).then((value) => {
            if (stopped || epoch.current !== version) return;
            setStudy((previous) => previous?.study_id !== id ? previous : { ...previous,
                trials: previous.trials.map((trial) => trial.index !== loadedIndex ? trial : {
                    ...trial, factor_exposures_pct_aum: value.factor_exposures_pct_aum,
                }),
            });
        }).catch((err) => {
            if (!stopped && epoch.current === version) setError(err.message);
        });
        return () => { stopped = true; };
    }, [study?.study_id, loadedIndex, hasExposures]);

    useEffect(() => {
        if (!study || !active(study.status)) return undefined;
        const id = study.study_id;
        let stopped = false;
        let timer;
        const poll = async () => {
            try {
                const result = requireStudy(await callBackend(path(id), 'GET'));
                if (!stopped) { install(result); setError(null); }
            } catch (err) {
                if (!stopped) {
                    if (optimizerErrorCode(err) === 'optimizer_design_gone') { forgetExpired(id, err.message); stopped = true; }
                    setError(err.message);
                }
            }
            if (!stopped) timer = setTimeout(poll, 1500);
        };
        timer = setTimeout(poll, 1000);
        return () => { stopped = true; clearTimeout(timer); };
    }, [study?.study_id, study?.status, install, forgetExpired]);

    async function perform(fn) {
        setBusy(true); setError(null);
        const version = epoch.current;
        try { return await fn(() => epoch.current === version); }
        catch (err) { if (epoch.current === version) setError(err.message); return null; }
        finally { if (epoch.current === version) setBusy(false); }
    }
    async function start(draft, settings) {
        ++selection.current;
        return perform(async (current) => {
            const command = await bridgeRef.current.buildCommand(draft);
            const identity = JSON.stringify({ command, settings });
            if (creating.current?.identity !== identity) creating.current = { identity, id: createOptimizerOperationId() };
            const result = await callBackend('optimizer_design/studies', 'POST', canonicalJsonObject({ study_id: creating.current.id, command, settings }));
            if (current()) { bridgeRef.current.clearCurrentResult(); setCandidate(null); setStage(null); install(result); creating.current = null; }
            return result;
        });
    }
    async function choose(index, chosenStage = null) {
        const version = ++selection.current;
        const id = study.study_id;
        const current = () => selection.current === version;
        setError(null); setBusy(true);
        try {
            const value = publicCandidate(await callBackend(`${path(id)}/trials/${index}`, 'GET'));
            if (!current()) return false;
            const chosen = chosenStage == null ? value : value.stages?.[chosenStage];
            if (value.status === 'verified' && chosen?.candidate && !await bridgeRef.current.selectCandidate({ studyId: id, index, stage: chosenStage, isCurrent: current })) return false;
            if (current()) {
                setCandidate(value); setStage(chosenStage);
                if (Array.isArray(value.factor_exposures_pct_aum)) setStudy((previous) => previous?.study_id !== id ? previous : {
                    ...previous, trials: previous.trials.map((trial) => trial.index !== index ? trial : {
                        ...trial, factor_exposures_pct_aum: value.factor_exposures_pct_aum,
                    }),
                });
            }
            return current();
        } catch (err) {
            if (current()) {
                if (optimizerErrorCode(err) === 'optimizer_design_gone') forgetExpired(id, err.message);
                else setError(err.message);
            }
            return false;
        }
        finally { if (current()) setBusy(false); }
    }
    async function control(action) {
        return perform(async (current) => {
            const value = await callBackend(`${path(study.study_id)}/${action}`, 'POST', {});
            if (current()) install(value);
        });
    }
    async function newStudy() {
        ++epoch.current; ++selection.current;
        return perform(async () => {
            await bridgeRef.current.discardExperiment();
            clear();
            return true;
        });
    }
    return { study, baseline, candidate, busy, error: error == null ? null : designPublicMessage(error), stage, start, choose, control, newStudy, clearError: () => setError(null) };
}
